/**
 * The stage's typography and its one numeric flourish.
 *
 * A slide is built from four pieces and rarely needs more: a kicker naming
 * what this beat is, a hero carrying the one thing to remember, a line or two
 * in her voice, and — where there is a figure worth staring at — a number that
 * counts itself up. Keeping them here rather than in each screen is what stops
 * the first meeting and the Sunday report from slowly becoming two different
 * designs.
 *
 * `step` is the element's place in the arrival order. Everything on a slide
 * lands one after another; a slide that assembles itself gets watched.
 */

import { cn } from '@gramas/ui'
import type { CSSProperties, ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { GhostReveal } from './GhostReveal.js'

export interface RiseProps {
  step?: number
  /** Extra wait in ms — for a beat beneath something that animates longer. */
  delayMs?: number
  className?: string
  children: ReactNode
}

/** Lands its children in the arrival order, and nothing else. */
export function Rise({ step = 0, delayMs, className, children }: RiseProps) {
  return (
    <div
      className={cn('stage-rise', className)}
      style={{ '--i': step, '--d': `${delayMs ?? 0}ms` } as CSSProperties}
    >
      {children}
    </div>
  )
}

/** What this beat is. Small, spaced, never the point. */
export function Kicker({ children, step = 0 }: { children: ReactNode; step?: number }) {
  return (
    <Rise step={step}>
      <p className="text-[11px] text-[var(--stage-mute)] uppercase tracking-[0.18em]">{children}</p>
    </Rise>
  )
}

/** The one thing to remember from this slide, in her voice. */
export function Hero({
  children,
  delayMs,
  className,
}: {
  children: string
  delayMs?: number
  className?: string
}) {
  return (
    <GhostReveal
      as="h2"
      sweep
      delayMs={delayMs}
      className={cn(
        'block font-display font-semibold text-[var(--stage-ink)]',
        'text-[clamp(1.9rem,9.5vw,2.9rem)] leading-[1.06] tracking-[-0.022em]',
        className,
      )}
    >
      {children}
    </GhostReveal>
  )
}

/** Her paragraph. Wider measure than the app's, because this one is read. */
export function Line({
  children,
  delayMs,
  className,
}: {
  children: string
  delayMs?: number
  className?: string
}) {
  return (
    <GhostReveal
      as="p"
      delayMs={delayMs}
      className={cn('block text-[15px] text-[var(--stage-dim)] leading-[1.55]', className)}
    >
      {children}
    </GhostReveal>
  )
}

/** An aside she adds after a beat — the same weight, her colour. */
export function Aside({ children, delayMs }: { children: string; delayMs?: number }) {
  return (
    <GhostReveal
      as="p"
      delayMs={delayMs ?? 520}
      className="block text-[15px] text-[var(--stage-glow)] leading-[1.5]"
    >
      {children}
    </GhostReveal>
  )
}

/**
 * A number that counts up to itself once, on arrival.
 *
 * `useCountUp` in the design system animates on *change* and starts at its
 * target, which is right for a ring that follows a diary and wrong here: a
 * report opens on the number, so the number has to arrive from somewhere.
 */
function useStageCount(target: number, delayMs: number, durationMs: number): number {
  const [shown, setShown] = useState(0)
  const frame = useRef(0)

  useEffect(() => {
    // No `matchMedia` means no way to ask, and something that cannot be asked
    // about motion preferences is not something that should be animating —
    // a test renderer, most often.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches !== false) {
      setShown(target)
      return
    }
    setShown(0)
    let start = 0
    const tick = (now: number) => {
      if (start === 0) start = now
      const t = Math.min(1, (now - start - delayMs) / durationMs)
      if (t > 0) setShown(target * (1 - (1 - t) ** 3))
      if (t < 1) frame.current = requestAnimationFrame(tick)
    }
    frame.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame.current)
  }, [target, delayMs, durationMs])

  return shown
}

export interface FigureProps {
  value: number
  /** Sits under the number: „kcal per dieną". */
  unit?: string
  /** Sits above it, small: „vidurkis". */
  label?: string
  /** Prefixes the figure — „×" for a count, „+" for a gap. */
  prefix?: string
  tone?: 'figure' | 'ink' | 'glow'
  delayMs?: number
}

const TONE: Record<NonNullable<FigureProps['tone']>, string> = {
  figure: 'text-[var(--stage-figure)] drop-shadow-[0_0_26px_rgba(201,247,90,0.35)]',
  ink: 'text-[var(--stage-ink)] drop-shadow-[0_0_26px_rgba(255,255,255,0.22)]',
  glow: 'text-[var(--stage-glow)] drop-shadow-[0_0_26px_rgba(139,116,255,0.45)]',
}

/** The figure a slide is built around, lit from behind. */
export function Figure({
  value,
  unit,
  label,
  prefix,
  tone = 'figure',
  delayMs = 240,
}: FigureProps) {
  const shown = useStageCount(value, delayMs, 1000)
  return (
    <div className="stage-halo" style={{ '--d': `${delayMs}ms` } as CSSProperties}>
      {label != null && (
        <p className="text-[11px] text-[var(--stage-mute)] uppercase tracking-[0.18em]">{label}</p>
      )}
      <p
        // `leading-*` has to follow `text-*` here: a size utility carries a
        // line height of its own in Tailwind v4, so `cn` (tailwind-merge)
        // treats the two as one group and keeps the last one written. Put the
        // leading first and it is dropped, leaving a 66px figure in a 22px
        // line box, sitting on top of its own label.
        className={cn(
          'tnum font-display font-semibold',
          'text-[clamp(3.2rem,17vw,5rem)] leading-none tracking-[-0.03em]',
          TONE[tone],
        )}
      >
        {prefix}
        {Math.round(shown)}
      </p>
      {unit != null && <p className="mt-1.5 text-[var(--stage-dim)] text-sm">{unit}</p>}
    </div>
  )
}

export interface BeatProps {
  /**
   * The picture this beat is built on, held in the upper half of the screen.
   * Words belong at the bottom, where the thumb is; the space above them is
   * where the thing being talked about goes. A slide with nothing up there is
   * allowed — a quote wants the emptiness — but it should be a decision.
   */
  top?: ReactNode
  children: ReactNode
  className?: string
}

/**
 * The standard slide: a figure floating in the upper half, words pushed to the
 * bottom third, where a thumb is and where a phone's own notifications are
 * not. Centred text was the old card's habit; left-aligned reads as someone
 * talking rather than as a poster.
 *
 * The top zone grows into whatever height is spare and never shrinks below its
 * own content — on a short screen the slide scrolls instead of squashing the
 * chart it exists to show.
 */
export function Beat({ top, children, className }: BeatProps) {
  return (
    <div className={cn('flex min-h-full flex-col', className)}>
      {top != null && (
        <div className="flex flex-1 shrink-0 flex-col justify-center pb-8">{top}</div>
      )}
      <div className={cn('flex flex-col gap-3.5 pb-6', top == null && 'flex-1 justify-end')}>
        {children}
      </div>
    </div>
  )
}

/**
 * Her, as a shape: something lit, breathing, with the room pushing out from it
 * in rings. Used where a beat is about her presence rather than about a number
 * — the first slide of the meeting, the last slide of both performances — so
 * the two sequences open and close on the same mark.
 */
export function Mark() {
  return (
    <Rise>
      <span aria-hidden="true" className="relative grid size-28 place-items-center">
        <span className="stage-ping absolute inset-0 rounded-full border border-[var(--stage-glow)]/45" />
        <span
          className="stage-ping absolute inset-2 rounded-full border border-[var(--stage-glow)]/30"
          style={{ '--d': '1400ms' } as CSSProperties}
        />
        <span className="stage-breathe block size-3.5 rounded-full bg-[var(--stage-glow)] shadow-[0_0_30px_8px_rgba(139,116,255,0.5)]" />
      </span>
    </Rise>
  )
}
